"use client";

import React, { useEffect, useState } from "react";
import { Box, Typography, Slider, InputBase } from "@mui/material";

interface WanLink {
  id: string;
  name: string;
  percentage?: number;
}

interface LinkBondingContentProps {
  links: WanLink[];
  onChange: (updatedLinks: WanLink[]) => void;
}

const LinkBondingContent: React.FC<LinkBondingContentProps> = ({ links, onChange }) => {
  const [sliders, setSliders] = useState<WanLink[]>([]);

  useEffect(() => {
    setSliders(links);
  }, [links]);

  const updatePercentage = (index: number, value: number) => {
    const updated = [...sliders];
    updated[index].percentage = Math.min(100, Math.max(0, value));
    setSliders(updated);
    onChange(updated);
  };

  const total = sliders.reduce((sum, s) => sum + (s.percentage || 0), 0);
  const getTotalColor = () =>
    total === 100 ? "#2AB75A" : total < 100 ? "#F6AD01" : "#F74040";

  return (
    <Box sx={{ px: 4, pt: 2, pb: 2 }}>
      {sliders.map((slider, i) => (
        <Box key={slider.id} sx={{ mb: 2 }}>
          <Typography sx={labelStyles}>{slider.name}</Typography>

          <Box sx={sliderRowStyles}>
            <Slider
              value={slider.percentage || 0}
              onChange={(_, val) => updatePercentage(i, typeof val === "number" ? val : 0)}
              min={0}
              max={100}
              sx={sliderStyles}
            />

            <InputBase
              type="number"
              value={slider.percentage?.toString() || "0"}
              onFocus={(e) => e.target.value === "0" && (e.target.value = "")}
              onChange={(e) => {
                const value = parseInt(e.target.value || "0", 10);
                if (!isNaN(value)) updatePercentage(i, value);
              }}
              sx={inputStyles}
            />
          </Box>
        </Box>
      ))}

      <Box sx={{ mt: 2 }}>
        <Typography sx={labelStyles}>Total</Typography>

        <Box sx={sliderRowStyles}>
          <Box sx={progressBarContainerStyles}>
            <Box
              sx={{
                width: `${Math.min(total, 100)}%`,
                height: "100%",
                backgroundColor: getTotalColor(),
                transition: "width 0.3s",
              }}
            />
          </Box>

          <Typography sx={totalValueStyles}>{total}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LinkBondingContent;

// 🔧 Style Constants
const labelStyles = {
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "10px",
  letterSpacing: "0%",
  color: "#45464E",
  mb: 0.5,
};

const sliderRowStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
};

const sliderStyles = {
  flex: 1,
  color: "#3E63DD",
  height: 4,
  "& .MuiSlider-track": { backgroundColor: "#3E63DD" },
  "& .MuiSlider-rail": { backgroundColor: "#E0E0E0" },
  "& .MuiSlider-thumb": {
    width: 28,
    height: 28,
    backgroundColor: "#ffffff",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    "&:hover, &.Mui-focusVisible, &.Mui-active": {
      boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.15)",
    },
  },
};

const inputStyles = {
  width: "52px",
  height: "28px",
  fontSize: "17px",
  fontWeight: 400,
  fontFamily: "Inter",
  lineHeight: "22px",
  textAlign: "center",
  px: 0.5,
  border: "1px solid #DADADA",
  borderRadius: "6px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
};

const progressBarContainerStyles = {
  flex: 1,
  height: "6px",
  backgroundColor: "#e0e0e0",
  borderRadius: "3px",
  overflow: "hidden",
};

const totalValueStyles = {
  width: "42px",
  fontSize: "17px",
  fontWeight: 400,
  fontFamily: "Inter",
  lineHeight: "22px",
  textAlign: "center",
  color: "#000000",
};
